import { Box, Container, Divider, Typography } from "@mui/material";
import Footer from "components/footer";
import keys from "locale/keys";
import { SyntheticEvent, useEffect } from "react";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import {
  ListItem,
  PoliciesContainer,
  PoliciesPageWrapper,
  PoliciesWrapper,
  SectionDescription,
  SectionTitle,
  StyledTab,
  StyledTabs,
  TabContent,
  UnorderedList,
} from "./styled.components";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`policy-tabpanel-${index}`}
      aria-labelledby={`policy-tab-${index}`}
    >
      {value === index && <>{children}</>}
    </div>
  );
};

const PoliciesContainer_Component = () => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");

  useEffect(() => {
    if (!tabParam) {
      setSearchParams({ tab: "terms" }, { replace: true });
    }
  }, [tabParam, setSearchParams]);

  const getTabValue = (tab: string | null) => {
    switch (tab) {
      case "terms":
        return 0;
      case "privacy":
        return 1;
      case "cookies":
        return 2;
      default:
        return 0;
    }
  };

  const calculateTabName = (val: number) => {
    switch (val) {
      case 1:
        return "privacy";
      case 2:
        return "cookies";
      default:
        return "terms";
    }
  };

  const tabValue = getTabValue(tabParam);

  const handleTabChange = (event: SyntheticEvent, newValue: number) => {
    setSearchParams({ tab: calculateTabName(newValue) });
  };

  return (
    <PoliciesPageWrapper>
      <Container maxWidth="lg">
        <Box sx={{ paddingTop: 3, paddingBottom: 3 }}>
          <Typography
            variant="h4"
            component="h1"
            sx={{ marginBottom: 2, fontWeight: 600 }}
          >
            {t(keys.policies_title)}
          </Typography>
          <Divider sx={{ marginBottom: 3 }} />
        </Box>
        <PoliciesWrapper>
          <PoliciesContainer>
            <StyledTabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="policy tabs"
            >
              <StyledTab
                label={t(keys.terms_of_service)}
                id="policy-tab-0"
                aria-controls="policy-tabpanel-0"
              />
              <StyledTab
                label={t(keys.privacy_policy)}
                id="policy-tab-1"
                aria-controls="policy-tabpanel-1"
              />
              <StyledTab
                label={t(keys.cookies_policy)}
                id="policy-tab-2"
                aria-controls="policy-tabpanel-2"
              />
            </StyledTabs>

            <TabPanel value={tabValue} index={0}>
              <TabContent>
                <SectionTitle variant="h5">
                  {t(keys.terms_of_service)}
                </SectionTitle>

                {keys.terms_sections.map((section: any, index: number) => (
                  <React.Fragment key={index}>
                    <SectionTitle variant="h6">{section.title}</SectionTitle>
                    {section.content.map(
                      (paragraph: string, pIndex: number) => (
                        <SectionDescription key={pIndex}>
                          {paragraph}
                        </SectionDescription>
                      ),
                    )}
                    {section.list && (
                      <UnorderedList>
                        {section.list.map((item: string, lIndex: number) => (
                          <ListItem key={lIndex}>{item}</ListItem>
                        ))}
                      </UnorderedList>
                    )}
                    {section.footer && (
                      <SectionDescription>{section.footer}</SectionDescription>
                    )}
                  </React.Fragment>
                ))}
              </TabContent>
            </TabPanel>

            <TabPanel value={tabValue} index={1}>
              <TabContent>
                <SectionTitle variant="h5">
                  {t(keys.privacy_policy)}
                </SectionTitle>

                {keys.privacy_sections.map((section: any, index: number) => (
                  <React.Fragment key={index}>
                    <SectionTitle variant="h6">{section.title}</SectionTitle>
                    {section.content.map(
                      (paragraph: string, pIndex: number) => (
                        <SectionDescription key={pIndex}>
                          {paragraph}
                        </SectionDescription>
                      ),
                    )}
                    {section.list && (
                      <UnorderedList>
                        {section.list.map((item: string, lIndex: number) => (
                          <ListItem key={lIndex}>{item}</ListItem>
                        ))}
                      </UnorderedList>
                    )}
                    {section.footer && (
                      <SectionDescription>{section.footer}</SectionDescription>
                    )}
                  </React.Fragment>
                ))}
              </TabContent>
            </TabPanel>

            <TabPanel value={tabValue} index={2}>
              <TabContent>
                <SectionTitle variant="h5">
                  {t(keys.cookies_policy)}
                </SectionTitle>

                {keys.cookies_sections.map((section: any, index: number) => (
                  <React.Fragment key={index}>
                    <SectionTitle variant="h6">{section.title}</SectionTitle>
                    {section.content.map(
                      (paragraph: string, pIndex: number) => (
                        <SectionDescription key={pIndex}>
                          {paragraph}
                        </SectionDescription>
                      ),
                    )}
                    {section.list && (
                      <UnorderedList>
                        {section.list.map((item: string, lIndex: number) => (
                          <ListItem key={lIndex}>{item}</ListItem>
                        ))}
                      </UnorderedList>
                    )}
                    {section.table && (
                      <Box sx={{ overflowX: "auto", mb: 2 }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            minWidth: 600,
                          }}
                        >
                          <thead>
                            <tr>
                              {section.table.headers.map(
                                (header: string, hIndex: number) => (
                                  <th
                                    key={hIndex}
                                    style={{
                                      border: "1px solid #ddd",
                                      padding: "8px",
                                      textAlign: "left",
                                      backgroundColor: "#f5f5f5",
                                    }}
                                  >
                                    {header}
                                  </th>
                                ),
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {section.table.rows.map(
                              (row: string[], rIndex: number) => (
                                <tr key={rIndex}>
                                  {row.map((cell: string, cIndex: number) => (
                                    <td
                                      key={cIndex}
                                      style={{
                                        border: "1px solid #ddd",
                                        padding: "8px",
                                      }}
                                    >
                                      {cell}
                                    </td>
                                  ))}
                                </tr>
                              ),
                            )}
                          </tbody>
                        </table>
                      </Box>
                    )}
                    {section.footer && (
                      <SectionDescription>{section.footer}</SectionDescription>
                    )}
                  </React.Fragment>
                ))}
              </TabContent>
            </TabPanel>
          </PoliciesContainer>
        </PoliciesWrapper>
      </Container>
      <Footer fullWidth={true} />
    </PoliciesPageWrapper>
  );
};

export default PoliciesContainer_Component;
